import { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, List, ListItem, ListItemText,
  IconButton, Tooltip, Divider, Chip, useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  reviseProjectDeployment,
  listProjectDeploymentVersions,
  rollbackProjectDeployment,
  retrainProjectDeployment,
  promoteProjectDeploymentModel,
} from "../utils/api";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";

/**
 * Chat-style refiner for an existing deployment composition. Owners type
 * free-form instructions ("group the chart by month", "drop the second
 * KPI"); the backend revises + revalidates and either replaces the
 * composition or returns a 422 with the validation report.
 *
 * The dialog also surfaces a rollback list — every revision archives the
 * prior version, so users can revert if a refinement was a mistake.
 */
export default function DeploymentReviseDialog({
  open, projectId, projectName, onClose, onRevised,
}) {
  const theme = useTheme();
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [narration, setNarration] = useState("");
  const [history, setHistory] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [retrainState, setRetrainState] = useState({ phase: "idle", run_id: null, error: "" });

  const fetchVersions = () =>
    listProjectDeploymentVersions(projectId)
      .then((res) => {
        setHistory(res?.history || []);
        setCurrentVersion(res?.current_version ?? null);
      })
      .catch(() => {});

  useEffect(() => {
    if (open && projectId) fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const handleRevise = async () => {
    const text = (instruction || "").trim();
    if (!text) return;
    setBusy(true);
    setError("");
    setNarration("");
    try {
      const res = await reviseProjectDeployment(projectId, text);
      setNarration(res?.narration || "Revision applied.");
      setInstruction("");
      await fetchVersions();
      if (onRevised) onRevised(res);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 422) {
        const v = e.response.data?.validation || {};
        const lintErrs = (v.lint?.lint_errors || []).join("; ");
        const dryErr = v.lint?.dry_run?.error;
        setError(
          `The revision didn't validate; current composition kept. ` +
          (lintErrs ? `Lint: ${lintErrs}. ` : "") +
          (dryErr ? `Dry-run: ${dryErr}` : "")
        );
      } else {
        setError(e?.response?.data?.error || e?.message || "Revise failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRetrain = async () => {
    setRetrainState({ phase: "queuing", run_id: null, error: "" });
    try {
      const res = await retrainProjectDeployment(projectId);
      setRetrainState({ phase: "queued", run_id: res?.ml_run_id || null, error: "" });
    } catch (e) {
      setRetrainState({
        phase: "error", run_id: null,
        error: e?.response?.data?.error || e?.message || "Retrain failed",
      });
    }
  };

  const handlePromote = async () => {
    const rid = retrainState.run_id;
    if (!rid) return;
    setRetrainState((s) => ({ ...s, phase: "promoting", error: "" }));
    try {
      await promoteProjectDeploymentModel(projectId, rid);
      setRetrainState({ phase: "promoted", run_id: rid, error: "" });
      await fetchVersions();
      if (onRevised) onRevised();
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 422
        ? "Composition doesn't validate against the new model; kept the previous one."
        : (e?.response?.data?.error || e?.message || "Promote failed");
      setRetrainState((s) => ({ ...s, phase: "error", error: msg }));
    }
  };

  const handleRollback = async (version) => {
    setBusy(true);
    setError("");
    setNarration("");
    try {
      await rollbackProjectDeployment(projectId, version);
      setNarration(`Rolled back to v${version}.`);
      await fetchVersions();
      if (onRevised) onRevised();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Rollback failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <EditNoteIcon sx={{ color: theme.palette.primary.main }} />
        Refine deployment
        {currentVersion != null && (
          <Chip label={`v${currentVersion}`} size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />
        )}
        <Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: 14, color: "text.secondary" }}>
          — {projectName || "this project"}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Describe a change in plain language. Examples: "group the trend chart by month",
          "drop the second KPI", "add a top-10 table sorted by score".
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          placeholder="What would you like to change?"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          disabled={busy}
          autoFocus
        />
        {narration && (
          <Alert severity="success" sx={{ mt: 1.5 }}>{narration}</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <ModelTrainingIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Retrain on latest data
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Trains a fresh model on the most recent rows. The deployment keeps
            running on the current model until you promote the new one.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRetrain}
              disabled={busy || retrainState.phase === "queuing" || retrainState.phase === "promoting"}
              sx={{ textTransform: "none" }}
            >
              {retrainState.phase === "queuing" ? "Queuing…" : "Retrain now"}
            </Button>
            {retrainState.phase === "queued" && (
              <>
                <Chip label={`run ${retrainState.run_id?.slice(0, 8)}`} size="small" />
                <Button
                  size="small"
                  onClick={handlePromote}
                  disabled={busy}
                  sx={{ textTransform: "none" }}
                >
                  Promote when ready
                </Button>
              </>
            )}
            {retrainState.phase === "promoting" && (
              <Typography variant="caption" color="text.secondary">
                Promoting…
              </Typography>
            )}
            {retrainState.phase === "promoted" && (
              <Typography variant="caption" color="success.main">
                Promoted.
              </Typography>
            )}
          </Box>
          {retrainState.error && (
            <Alert severity="error" sx={{ mt: 1 }}>{retrainState.error}</Alert>
          )}
        </Box>

        {history.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <HistoryIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Previous versions
              </Typography>
            </Box>
            <List dense>
              {history.map((h) => (
                <ListItem
                  key={h.composition_version}
                  secondaryAction={
                    <Tooltip title={`Roll back to v${h.composition_version}`}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={busy}
                          onClick={() => handleRollback(h.composition_version)}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={`v${h.composition_version} · ${h.shape || "—"}`}
                    secondary={
                      h.archived_at
                        ? `archived ${new Date(h.archived_at).toLocaleString()}`
                        : null
                    }
                    primaryTypographyProps={{ fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained"
          onClick={handleRevise}
          disabled={busy || !instruction.trim()}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: "none", boxShadow: "none" }}
        >
          {busy ? "Working…" : "Apply change"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
